import type { Lesson } from "@repo/hindi-shared";
import { lessonList, letterById } from "@repo/hindi-shared";
import { useState } from "react";

import { navigate } from "../router";
import { loadState } from "../state/store";
import { DualLabel } from "../ui/DualLabel";
import { GlyphTile } from "../ui/GlyphTile";
import { RubricNumeral } from "../ui/RubricNumeral";
import { Shirorekha } from "../ui/Shirorekha";
import { UNIT_LABELS } from "../unitLabels";

interface Unit {
  unit: number;
  lessons: Lesson[];
}

function groupByUnit(lessons: readonly Lesson[]): Unit[] {
  const units: Unit[] = [];
  for (const lesson of lessons) {
    const existing = units.find((u) => u.unit === lesson.unit);
    if (existing !== undefined) {
      existing.lessons.push(lesson);
    } else {
      units.push({ unit: lesson.unit, lessons: [lesson] });
    }
  }
  return units;
}

type RowStatus = "done" | "next" | "locked";

function statusFor(
  lesson: Lesson,
  completed: ReadonlySet<string>,
  nextLessonId: string | undefined,
): RowStatus {
  if (completed.has(lesson.id)) return "done";
  if (lesson.id === nextLessonId) return "next";
  return "locked";
}

const UNITS = groupByUnit(lessonList);

/**
 * Lessons — the curriculum as chapters. Each unit is a chapter (RubricNumeral
 * + Hindi title + English subtitle); each lesson is a row marked done (ink
 * check), next (sindoor "अगला"), or locked (quiet, but tappable to preview
 * its items — no practice). Tapping the next lesson starts `/session`
 * (the runner naturally serves whatever lesson is next).
 */
export function Lessons() {
  const store = loadState();
  const completed = new Set(store.completedLessonIds);
  const nextLesson = lessonList.find((lesson) => !completed.has(lesson.id));
  const [previewLessonId, setPreviewLessonId] = useState<string | null>(null);

  function handleRowClick(lesson: Lesson, status: RowStatus): void {
    if (status === "next") {
      navigate("/session");
      return;
    }
    setPreviewLessonId((current) => (current === lesson.id ? null : lesson.id));
  }

  return (
    <main className="paper-grain min-h-dvh px-6 py-12">
      <section className="mx-auto w-full max-w-2xl">
        <Shirorekha className="pt-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Home"
            className="inline-flex items-center gap-1.5 transition-colors duration-150 hover:text-sindoor"
          >
            <span aria-hidden="true" className="text-xs text-ink-soft">
              ←
            </span>
            <DualLabel
              en="Hindi"
              hi="हिन्दी"
              className="text-xs text-ink-soft uppercase"
            />
          </button>
          <h1 className="mt-4 font-display text-5xl leading-[0.95] text-ink">
            <DualLabel en="Lessons" hi="पाठ" />
          </h1>
        </Shirorekha>

        <div className="mt-10 flex flex-col gap-10">
          {UNITS.map((unit) => {
            const label = UNIT_LABELS[unit.unit];
            return (
              <div key={unit.unit}>
                <Shirorekha>
                  <p className="font-dev text-lg text-ink">
                    <RubricNumeral value={unit.unit} className="mr-2" />
                    <DualLabel en={label?.en ?? ""} hi={label?.hi} />
                  </p>
                </Shirorekha>

                <ul className="mt-4 flex flex-col">
                  {unit.lessons.map((lesson) => {
                    const status = statusFor(lesson, completed, nextLesson?.id);
                    const isPreviewOpen = previewLessonId === lesson.id;
                    return (
                      <li key={lesson.id} className="border-b border-rule py-3">
                        <button
                          type="button"
                          onClick={() => handleRowClick(lesson, status)}
                          className={`flex w-full items-center justify-between gap-3 text-left font-latin text-base transition-colors duration-150 hover:text-sindoor ${
                            status === "locked" ? "text-ink-soft" : "text-ink"
                          }`}
                        >
                          <DualLabel
                            variant="inline"
                            en={lesson.titleEn}
                            hi={lesson.titleHi}
                          />
                          {status === "done" && (
                            <span aria-label="Done" className="text-ink">
                              ✓
                            </span>
                          )}
                          {status === "next" && (
                            <span className="font-dev text-sindoor">
                              <DualLabel variant="inline" en="Next" hi="अगला" />
                            </span>
                          )}
                        </button>

                        {status === "locked" && isPreviewOpen && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {lesson.newItemIds.length === 0 ? (
                              <p className="font-latin text-sm text-ink-soft italic">
                                <span lang="en">Review — no new letters</span>
                              </p>
                            ) : (
                              lesson.newItemIds.map((itemId) => {
                                const glyph = glyphForItemId(itemId);
                                if (glyph === null) return null;
                                return (
                                  <GlyphTile
                                    key={itemId}
                                    glyph={glyph}
                                    size="sm"
                                    disabled
                                  />
                                );
                              })
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}

/** Resolve a lesson-preview item id to its glyph, or `null` for a word/sentence id (skipped in the preview grid). */
function glyphForItemId(itemId: string): string | null {
  return letterById.get(itemId)?.glyph ?? null;
}
